import { RentVsBuyVariables } from "./RentVsBuyCalculator.constants.esm.js";
const baseExpenses = (step, maxVal, currentState) => [{
  id: RentVsBuyVariables.PurchasePrice,
  step,
  defaultValue: currentState.purchasePrice,
  max: maxVal,
  percentageQuestion: false,
  perMonth: false,
  sliderQuestion: "What is your comfortable home price?"
}, {
  id: RentVsBuyVariables.DownPaymentPercent,
  step,
  defaultValue: currentState.downPaymentPercent,
  max: maxVal,
  percentageQuestion: true,
  perMonth: false,
  sliderQuestion: "How much is your down payment?"
}, {
  id: RentVsBuyVariables.MonthlyRent,
  step,
  defaultValue: currentState.monthlyRent,
  max: maxVal,
  percentageQuestion: false,
  perMonth: true,
  sliderQuestion: "What is your comfortable monthly rent?"
}];
const growthRates = (step, maxVal, currentState) => [{
  id: RentVsBuyVariables.InitialHomeAppreciationRate,
  step,
  defaultValue: currentState.initialHomeAppreciationRate,
  max: maxVal,
  percentageQuestion: true,
  sliderQuestion: "First-year home value forecast?",
  toolTipHeader: "FIRST-YEAR HOME VALUE FORECAST",
  toolTipStatement: `The rate the home's value will grow for the first few years after buying. We use historical home appreciation rates to predict what will happen to the home's price in the long term.`
}, {
  id: RentVsBuyVariables.InitialRentAppreciationRate,
  step,
  defaultValue: currentState.initialRentAppreciationRate,
  max: maxVal,
  percentageQuestion: true,
  sliderQuestion: "First-year rent forecast?",
  toolTipHeader: "FIRST-YEAR RENT FORECAST",
  toolTipStatement: "The rate rent will increase for the first few years of renting. We use historical rent appreciation rates to predict how fast rent will go up in the long term."
}, {
  id: RentVsBuyVariables.InvestmentReturnRate,
  step,
  defaultValue: currentState.investmentReturnRate,
  max: maxVal,
  percentageQuestion: true,
  sliderQuestion: "Investment rate?",
  toolTipHeader: "INVESTMENT RATE",
  toolTipStatement: "How much you earn per year from any money you save in a bank, stocks, bonds, or other form of investment."
}];
const buyingExpenses = (currentState) => [{
  id: RentVsBuyVariables.MortgageRate,
  inputLabel: "Mortgage rate",
  defaultValue: currentState.mortgageRate,
  percentageQuestion: true,
  perMonth: false,
  maxInputLength: 8,
  toolTipHeader: "MORTGAGE RATE",
  toolTipStatement: "The rate on your purchase mortgage. Private mortgage insurance (PMI) will automatically be added based on your down payment percentage."
}, {
  id: RentVsBuyVariables.HoaDues,
  inputLabel: "HOA fee",
  defaultValue: currentState.hoaDues,
  percentageQuestion: false,
  perMonth: true,
  maxInputLength: 5,
  toolTipHeader: "HOA FEES",
  toolTipStatement: `Any monthly fees or assessments imposed by a home's homeowners association (HOA). Customary for condos and townhomes, they're less common on single-family homes.`
}, {
  id: RentVsBuyVariables.PropertyTaxRate,
  inputLabel: "Annual property tax rate",
  defaultValue: currentState.propertyTaxRate,
  percentageQuestion: true,
  perMonth: false,
  maxInputLength: 8,
  toolTipHeader: "PROPERTY TAX RATE",
  toolTipStatement: "Rate is a percentage determined by local taxing authority. It is then calculated with a home's appraised value to determine tax payment."
}, {
  id: RentVsBuyVariables.MaintenanceCostsPercent,
  inputLabel: "Maintenance rate",
  defaultValue: currentState.maintenanceCostsPercent,
  percentageQuestion: true,
  perMonth: false,
  maxInputLength: 5,
  toolTipHeader: "MAINTENANCE RATE",
  toolTipStatement: "To maintain the quality of your home, it is assumed you will spend x% of your initial home value in the first year. In each subsequent year, we inflate this maintenance spending with annual inflation."
}, {
  id: RentVsBuyVariables.PurchaseCostsPercent,
  inputLabel: "Purchase costs percent",
  defaultValue: currentState.purchaseCostsPercent,
  percentageQuestion: true,
  perMonth: false,
  maxInputLength: 5,
  toolTipHeader: "PURCHASE COSTS PERCENT",
  toolTipStatement: "Costs of buying the home as a percentage of the purchase price, including lender fees, appraisals, inspections, and other closing costs."
}, {
  id: RentVsBuyVariables.SellingCostsPercent,
  inputLabel: "Selling costs percent",
  defaultValue: currentState.sellingCostsPercent,
  percentageQuestion: true,
  perMonth: false,
  maxInputLength: 3,
  toolTipHeader: "SELLING COSTS PERCENT",
  toolTipStatement: "Costs of selling the home as a percentage of the sale price, including any agent commission."
}, {
  id: RentVsBuyVariables.HomeownersInsurancePercent,
  inputLabel: `Homeowner's insurance cost`,
  defaultValue: Math.ceil(currentState.homeownersInsurancePercent * currentState.purchasePrice / 100),
  percentageQuestion: false,
  perMonth: false,
  maxInputLength: 8,
  toolTipHeader: "HOMEOWNERS INSURANCE",
  toolTipStatement: "Homeowners insurance is required for most loans and is purchased annually. If your lender requires additional flood or earthquake insurance, include those costs here as well."
}, {
  id: RentVsBuyVariables.MarginalTaxRate,
  inputLabel: "Marginal tax rate",
  defaultValue: currentState.marginalTaxRate,
  percentageQuestion: true,
  perMonth: false,
  maxInputLength: 3,
  toolTipHeader: "MARGINAL TAX RATE",
  toolTipStatement: "The marginal tax rate varies according to your income bracket. The default value used is 25%. If, in a given year, total mortgage interest payments plus property taxes paid exceed the standard deduction, then a household's tax bill will be lower by 25% of the excess."
}, {
  id: RentVsBuyVariables.RenovationCostsPercent,
  inputLabel: "Annual renovation costs",
  defaultValue: Math.ceil(currentState.renovationCostsPercent * currentState.purchasePrice / 100),
  percentageQuestion: false,
  perMonth: false,
  maxInputLength: 6,
  toolTipHeader: "RENOVATION COST RATE",
  toolTipStatement: "To improve the quality of your home, it is assumed you will spend x% of your initial home value in the first year. In each subsequent year, we inflate this renovation spending with annual inflation."
}];
const rentalExpenses = (currentState) => [{
  id: RentVsBuyVariables.RentingUtilities,
  inputLabel: "Renting utility costs",
  defaultValue: currentState.rentingUtilities,
  percentageQuestion: false,
  perMonth: true,
  maxInputLength: 6,
  toolTipHeader: "RENTING UTILITY COSTS",
  toolTipStatement: "Your expected monthly spending on utilities you expect to pay as a renter during the first year. After the first year, we inflate this spending with the growth in overall prices. If you expect to spend no less on utilities as a renter, leave at 0."
}, {
  id: RentVsBuyVariables.RentBrokerFee,
  inputLabel: "Rent broker fee",
  defaultValue: currentState.rentBrokerFee,
  percentageQuestion: false,
  perMonth: true,
  maxInputLength: 6,
  toolTipHeader: "BROKER FEE",
  toolTipStatement: "Typical in large cities. Some condos or apartments require the services of a broker to lease a unit. Provide this as a number of months of rent."
}, {
  id: RentVsBuyVariables.RentersInsuranceRate,
  inputLabel: `Renter's insurance cost`,
  defaultValue: Math.ceil(currentState.rentersInsuranceRate * currentState.monthlyRent / 100) ?? 0,
  perMonth: true,
  percentageQuestion: false,
  maxInputLength: 6,
  toolTipHeader: `RENTER'S INSURANCE COST`,
  toolTipStatement: "Your monthly spending on renter's insurance. After the first year, we inflate this spending with the growth in overall prices."
}];
export {
  baseExpenses,
  buyingExpenses,
  growthRates,
  rentalExpenses
};
